// SkipEnquiriesListPage — /skip/enquiries.

import { useMemo, useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Tabs } from '../../../components/ui/Tabs/Tabs';
import { useSkipEnquiries } from '../hooks/useSkipEnquiries';
import { usePendingRequests } from '../hooks/useSkipJobs';
import { useSkipJobTypes } from '../hooks/useSkipJobTypes';
import { SkipEnquiryStatusBadge } from './components/SkipEnquiryStatusBadge';
import { ProcessImmediateEnquiryModal } from './components/ProcessImmediateEnquiryModal';
import { SkipJobStatusBadge } from '../jobs/components/SkipJobStatusBadge';
import { ProcessCollectionModal } from '../jobs/components/ProcessCollectionModal';
import { ProcessExchangeModal } from '../jobs/components/ProcessExchangeModal';
import { ProcessTipReturnModal } from '../jobs/components/ProcessTipReturnModal';
import type { SkipEnquirySummary, SkipJobSummary } from '../../../types/api';
import styles from './SkipEnquiriesListPage.module.css';

// Feature 2 — sibling of the SkipJobsListPage subheader. Same set of
// quick links, ordered to lead with the page-local create flow.
function SubheaderLinks(): JSX.Element {
  return (
    <nav className={styles.subheader} aria-label="Skip module quick links">
      <NavLink
        to="/skip/enquiries/create"
        className={({ isActive }) =>
          [styles.subheaderLink, isActive ? styles.subheaderLinkActive : '']
            .filter(Boolean)
            .join(' ')
        }
      >
        Add Enquiry
      </NavLink>
      <NavLink
        to="/skip/jobs/new"
        className={({ isActive }) =>
          [styles.subheaderLink, isActive ? styles.subheaderLinkActive : '']
            .filter(Boolean)
            .join(' ')
        }
      >
        Add Job
      </NavLink>
      <NavLink
        to="/skip/jobs/active"
        className={({ isActive }) =>
          [styles.subheaderLink, isActive ? styles.subheaderLinkActive : '']
            .filter(Boolean)
            .join(' ')
        }
      >
        Active Jobs
      </NavLink>
    </nav>
  );
}

const STATUSES = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'converted', label: 'Converted' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired', label: 'Expired' },
];

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

type ActiveTab = 'enquiries' | 'pending';

interface PendingRequestModalTarget {
  kind: 'collection' | 'exchange' | 'tip_return';
  jobEncId: string;
  jobNumber: string;
  customerName: string;
  /** Bug 3 — pass the parent job's skip_size_enc_id down so the
   *  ProcessExchangeModal can server-filter the replacement-unit
   *  dropdown to matching units. Null when the job has no size set
   *  (legacy data) — the picker stays disabled in that case. */
  skipSizeEncId: string | null;
}

export function SkipEnquiriesListPage(): JSX.Element {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActiveTab>('enquiries');
  // Default the page to "Open" enquiries — the most common starting
  // view for staff working a queue. Other statuses remain accessible
  // via the dropdown.
  const [status, setStatus] = useState('open');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  // Feature 2 — Pending Requests tab. Separate state slot so the
  // tab survives the existing search/page state on the Enquiries
  // tab without leaking filters between the two contexts.
  const [pendingTarget, setPendingTarget] =
    useState<PendingRequestModalTarget | null>(null);

  // The process_immediately flag lives on skip_job_types, not the
  // enquiry summary. Look it up by job_type key on the client; the
  // map is small (≤ a dozen types per tenant).
  const jobTypesQuery = useSkipJobTypes({ forSelection: true });
  const jobTypeImmediateMap = useMemo(() => {
    const map = new Map<string, { processImmediately: boolean; name: string }>();
    for (const t of jobTypesQuery.data?.jobTypes ?? []) {
      map.set(t.key, { processImmediately: t.process_immediately, name: t.name });
    }
    return map;
  }, [jobTypesQuery.data]);

  const [immediateTarget, setImmediateTarget] = useState<{
    encId: string;
    enquiryRef: string;
    customerName: string;
    jobTypeName: string;
    /** Bug 3 — propagated to ProcessImmediateEnquiryModal so the
     *  inventory dropdown filters by the enquiry's chosen size. */
    skipSizeEncId: string | null;
  } | null>(null);

  const filters: Record<string, string | number> = { page, limit: 25 };
  if (status) filters['status'] = status;
  if (q) filters['q'] = q;

  const enquiries = useSkipEnquiries(filters);
  const pendingRequests = usePendingRequests({});
  const pendingRows = pendingRequests.data?.jobs ?? [];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Pending Enquiries"
        subtitle="Manage customer enquiries, follow-ups and conversions"
        actions={
          <Button variant="primary" icon={Plus} onClick={() => navigate('/skip/enquiries/create')}>
            New Enquiry
          </Button>
        }
      />

      <SubheaderLinks />

      <Tabs
        ariaLabel="Skip enquiries tabs"
        value={activeTab}
        onChange={(id) => setActiveTab(id as ActiveTab)}
        items={[
          { id: 'enquiries', label: 'Enquiries' },
          {
            id: 'pending',
            label: 'Pending Requests',
            count: pendingRows.length,
          },
        ]}
      />

      {activeTab === 'pending' ? (
        <Card>
          {pendingRequests.isLoading ? (
            <p className={styles.empty}>Loading…</p>
          ) : pendingRows.length === 0 ? (
            <p className={styles.empty}>No pending requests at this time.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Job #</th>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Active Request</th>
                  <th>Delivery</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {pendingRows.map((j: SkipJobSummary) => {
                  const requestKind: 'collection' | 'exchange' | 'tip_return' | null =
                    j.collection_status === 'requested'
                      ? 'collection'
                      : j.exchange_status === 'requested'
                        ? 'exchange'
                        : j.tip_return_status === 'requested'
                          ? 'tip_return'
                          : null;
                  return (
                    <tr
                      key={j.enc_id}
                      className={j.is_priority ? styles.priorityRow : undefined}
                    >
                      <td className={styles.cellMono}>
                        <Link to={`/skip/jobs/${encodeURIComponent(j.enc_id)}`}>
                          {j.job_number}
                        </Link>
                      </td>
                      <td>
                        {j.customer_name}
                        {j.is_priority ? (
                          <span className={styles.priorityChip}>PRIORITY</span>
                        ) : null}
                      </td>
                      <td>{j.job_type.replaceAll('_', ' ')}</td>
                      <td>
                        <SkipJobStatusBadge status={j.status} />
                      </td>
                      <td>
                        {j.collection_status === 'requested' ? (
                          <span className={styles.requestBadgeCollection}>
                            Collection Requested
                          </span>
                        ) : null}
                        {j.exchange_status === 'requested' ? (
                          <span className={styles.requestBadgeExchange}>
                            Exchange Requested
                          </span>
                        ) : null}
                        {j.tip_return_status === 'requested' ? (
                          <span className={styles.requestBadgeTipReturn}>
                            Tip &amp; Return Requested
                          </span>
                        ) : null}
                      </td>
                      <td>{fmtDate(j.delivery_date)}</td>
                      <td className={styles.actions}>
                        <Link to={`/skip/jobs/${encodeURIComponent(j.enc_id)}`}>
                          View
                        </Link>
                        {requestKind === 'collection' ? (
                          <Button
                            variant="primary"
                            type="button"
                            onClick={() =>
                              setPendingTarget({
                                kind: 'collection',
                                jobEncId: j.enc_id,
                                jobNumber: j.job_number,
                                customerName: j.customer_name,
                                skipSizeEncId: j.skip_size_enc_id,
                              })
                            }
                          >
                            Mark Collected
                          </Button>
                        ) : null}
                        {requestKind === 'exchange' ? (
                          <Button
                            variant="primary"
                            type="button"
                            onClick={() =>
                              setPendingTarget({
                                kind: 'exchange',
                                jobEncId: j.enc_id,
                                jobNumber: j.job_number,
                                customerName: j.customer_name,
                                skipSizeEncId: j.skip_size_enc_id,
                              })
                            }
                          >
                            Process Exchange
                          </Button>
                        ) : null}
                        {requestKind === 'tip_return' ? (
                          <Button
                            variant="primary"
                            type="button"
                            onClick={() =>
                              setPendingTarget({
                                kind: 'tip_return',
                                jobEncId: j.enc_id,
                                jobNumber: j.job_number,
                                customerName: j.customer_name,
                                skipSizeEncId: j.skip_size_enc_id,
                              })
                            }
                          >
                            Process Tip &amp; Return
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      ) : null}

      {activeTab === 'enquiries' ? (
        <Card className={styles.filters}>
          <div className={styles.filterRow}>
            <input
              type="search"
              placeholder="Search enquiries…"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              className={styles.search}
              aria-label="Search enquiries"
            />
            <select
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              className={styles.statusSelect}
              aria-label="Filter by status"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </Card>
      ) : null}

      {activeTab === 'enquiries' ? (
      <Card>
        {enquiries.isLoading ? (
          <p className={styles.empty}>Loading…</p>
        ) : (enquiries.data?.enquiries.length ?? 0) === 0 ? (
          <p className={styles.empty}>No enquiries match your filters.</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Enquiry #</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Postcode</th>
                <th>Follow-up</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {enquiries.data?.enquiries.map((e: SkipEnquirySummary) => {
                const jobTypeMeta = e.job_type
                  ? jobTypeImmediateMap.get(e.job_type)
                  : undefined;
                const isImmediate = jobTypeMeta?.processImmediately === true;
                const isProcessable =
                  e.status !== 'converted' &&
                  e.status !== 'cancelled' &&
                  e.status !== 'expired' &&
                  e.job_type !== null;
                return (
                  <tr
                    key={e.enc_id}
                    className={e.is_priority ? styles.priorityRow : undefined}
                  >
                    <td className={styles.cellMono}>
                      <Link to={`/skip/enquiries/${encodeURIComponent(e.enc_id)}`}>
                        {e.enquiry_number}
                      </Link>
                    </td>
                    <td>
                      {e.customer_name}
                      {e.is_priority ? <span className={styles.priorityChip}>PRIORITY</span> : null}
                    </td>
                    <td>{e.skip_stock_type_name ?? e.skip_type ?? '—'}</td>
                    <td>{e.customer_postcode}</td>
                    <td>{fmtDate(e.follow_up_date)}</td>
                    <td>
                      <SkipEnquiryStatusBadge status={e.status} />
                    </td>
                    <td className={styles.actions}>
                      <Link to={`/skip/enquiries/${encodeURIComponent(e.enc_id)}`}>View</Link>
                      {isProcessable ? (
                        isImmediate ? (
                          <Button
                            variant="primary"
                            type="button"
                            onClick={() =>
                              setImmediateTarget({
                                encId: e.enc_id,
                                enquiryRef: e.enquiry_number,
                                customerName: e.customer_name,
                                jobTypeName: jobTypeMeta?.name ?? e.job_type ?? '',
                                skipSizeEncId: e.skip_size_enc_id,
                              })
                            }
                          >
                            Process Now
                          </Button>
                        ) : (
                          <Button
                            variant="primary"
                            type="button"
                            onClick={() =>
                              navigate(
                                `/skip/enquiries/${encodeURIComponent(e.enc_id)}/process`,
                              )
                            }
                          >
                            Process
                          </Button>
                        )
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {enquiries.data && enquiries.data.pagination.totalPages > 1 ? (
          <div className={styles.pagination}>
            <Button
              variant="secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className={styles.pageInfo}>
              Page {page} of {enquiries.data.pagination.totalPages} (
              {enquiries.data.pagination.total} total)
            </span>
            <Button
              variant="secondary"
              disabled={page >= enquiries.data.pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </Card>
      ) : null}

      {pendingTarget?.kind === 'collection' ? (
        <ProcessCollectionModal
          isOpen
          jobEncId={pendingTarget.jobEncId}
          jobNumber={pendingTarget.jobNumber}
          customerName={pendingTarget.customerName}
          onClose={() => setPendingTarget(null)}
          onSuccess={() => void pendingRequests.refetch()}
        />
      ) : null}
      {pendingTarget?.kind === 'exchange' ? (
        <ProcessExchangeModal
          isOpen
          jobEncId={pendingTarget.jobEncId}
          jobNumber={pendingTarget.jobNumber}
          customerName={pendingTarget.customerName}
          skipSizeEncId={pendingTarget.skipSizeEncId}
          onClose={() => setPendingTarget(null)}
          onSuccess={() => void pendingRequests.refetch()}
        />
      ) : null}
      {pendingTarget?.kind === 'tip_return' ? (
        <ProcessTipReturnModal
          isOpen
          jobEncId={pendingTarget.jobEncId}
          jobNumber={pendingTarget.jobNumber}
          customerName={pendingTarget.customerName}
          onClose={() => setPendingTarget(null)}
          onSuccess={() => void pendingRequests.refetch()}
        />
      ) : null}

      {immediateTarget ? (
        <ProcessImmediateEnquiryModal
          isOpen
          enquiryEncId={immediateTarget.encId}
          enquiryRef={immediateTarget.enquiryRef}
          customerName={immediateTarget.customerName}
          jobTypeName={immediateTarget.jobTypeName}
          skipSizeEncId={immediateTarget.skipSizeEncId}
          onClose={() => setImmediateTarget(null)}
        />
      ) : null}
    </div>
  );
}
